import { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

// ─── Design tokens ───────────────────────────────────────────
const TOKENS = {
  primary:   '#3b5bdb',
  accent:    '#f59e0b',
  success:   '#10b981',
  danger:    '#ef4444',
  warning:   '#f59e0b',
  info:      '#06b6d4',
  radius:    12,
};

function buildTheme(dark) {
  const bg    = dark ? '#080d1a' : '#f0f4ff';
  const paper = dark ? '#0f1729' : '#ffffff';
  const text  = dark ? '#e8eeff' : '#0c1120';

  return createTheme({
    palette: {
      mode: dark ? 'dark' : 'light',
      primary: {
        main:  TOKENS.primary,
        light: dark ? '#6382f5' : '#5c7cfa',
        dark:  '#2f4ac9',
        contrastText: '#ffffff',
      },
      secondary: {
        main:  TOKENS.accent,
        light: '#fbbf24',
        dark:  '#d97706',
        contrastText: '#0c1120',
      },
      error:   { main: TOKENS.danger  },
      warning: { main: TOKENS.warning },
      info:    { main: TOKENS.info    },
      success: { main: TOKENS.success },
      background: {
        default: bg,
        paper:   paper,
      },
      text: {
        primary:   text,
        secondary: dark ? '#8fa3cc' : '#4a5568',
        disabled:  dark ? '#3d5275' : '#94a3b8',
      },
      divider: dark ? 'rgba(99,140,255,0.1)' : 'rgba(59,91,219,0.1)',
    },

    typography: {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      h1: { fontFamily: "'Syne', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' },
      h2: { fontFamily: "'Syne', sans-serif", fontWeight: 700, letterSpacing: '-0.025em' },
      h3: { fontFamily: "'Syne', sans-serif", fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontFamily: "'Syne', sans-serif", fontWeight: 700, letterSpacing: '-0.015em' },
      h5: { fontFamily: "'Syne', sans-serif", fontWeight: 600, letterSpacing: '-0.01em' },
      h6: { fontFamily: "'Syne', sans-serif", fontWeight: 600, letterSpacing: '-0.005em' },
      subtitle1: { fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 600, letterSpacing: '-0.005em' },
      body1:     { fontWeight: 400, letterSpacing: '-0.01em', lineHeight: 1.65 },
      body2:     { fontWeight: 400, letterSpacing: '-0.005em', lineHeight: 1.6 },
      button:    { fontWeight: 600, letterSpacing: '0em', textTransform: 'none' },
      caption:   { letterSpacing: '0.01em' },
      overline:  { fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, letterSpacing: '0.12em', fontSize: '0.7rem' },
    },

    shape: { borderRadius: TOKENS.radius },

    shadows: [
      'none',
      '0 1px 3px rgba(12,17,32,0.08), 0 1px 2px rgba(12,17,32,0.04)',
      '0 2px 6px rgba(12,17,32,0.08), 0 1px 3px rgba(12,17,32,0.05)',
      '0 4px 12px rgba(12,17,32,0.1), 0 2px 6px rgba(12,17,32,0.06)',
      '0 6px 18px rgba(12,17,32,0.1)',
      '0 8px 24px rgba(12,17,32,0.12)',
      '0 12px 32px rgba(12,17,32,0.12)',
      '0 16px 40px rgba(12,17,32,0.12)',
      '0 20px 48px rgba(12,17,32,0.14)',
      '0 24px 56px rgba(12,17,32,0.14)',
      '0 28px 64px rgba(12,17,32,0.14)',
      '0 32px 72px rgba(12,17,32,0.16)',
      '0 36px 80px rgba(12,17,32,0.16)',
      '0 40px 88px rgba(12,17,32,0.16)',
      '0 44px 96px rgba(12,17,32,0.18)',
      '0 48px 104px rgba(12,17,32,0.18)',
      '0 52px 112px rgba(12,17,32,0.18)',
      '0 56px 120px rgba(12,17,32,0.2)',
      '0 60px 128px rgba(12,17,32,0.2)',
      '0 64px 136px rgba(12,17,32,0.2)',
      '0 68px 144px rgba(12,17,32,0.22)',
      '0 72px 152px rgba(12,17,32,0.22)',
      '0 76px 160px rgba(12,17,32,0.22)',
      '0 80px 168px rgba(12,17,32,0.24)',
      '0 84px 176px rgba(12,17,32,0.24)',
    ],

    components: {
      // ── AppBar ──────────────────────────────────────────────
      MuiAppBar: {
        styleOverrides: {
          root: () => ({
            background: dark
              ? 'rgba(8,13,26,0.85)'
              : 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${dark ? 'rgba(99,140,255,0.1)' : 'rgba(59,91,219,0.1)'}`,
            boxShadow: 'none',
            color: dark ? '#e8eeff' : '#0c1120',
          }),
        },
      },

      // ── Card ────────────────────────────────────────────────
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: TOKENS.radius + 4,
            border: `1px solid ${dark ? 'rgba(99,140,255,0.08)' : 'rgba(59,91,219,0.08)'}`,
            boxShadow: dark
              ? '0 4px 24px rgba(0,0,0,0.3)'
              : '0 4px 24px rgba(12,17,32,0.07)',
            backgroundImage: 'none',
            transition: 'box-shadow 0.25s ease, transform 0.25s ease',
            '&:hover': {
              boxShadow: dark
                ? '0 8px 32px rgba(0,0,0,0.4)'
                : '0 8px 32px rgba(12,17,32,0.12)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },

      // ── Paper ───────────────────────────────────────────────
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            transition: 'background-color 0.3s ease, box-shadow 0.25s ease',
            border: `1px solid ${dark ? 'rgba(99,140,255,0.06)' : 'rgba(59,91,219,0.06)'}`,
          },
          elevation1: {
            boxShadow: dark
              ? '0 2px 8px rgba(0,0,0,0.25)'
              : '0 2px 8px rgba(12,17,32,0.07)',
          },
        },
      },

      // ── Button ──────────────────────────────────────────────
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: TOKENS.radius,
            padding: '8px 20px',
            transition: 'all 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${TOKENS.primary} 0%, #5c7cfa 100%)`,
            boxShadow: `0 4px 14px ${alpha(TOKENS.primary, 0.3)}`,
            '&:hover': {
              background: `linear-gradient(135deg, #2f4ac9 0%, ${TOKENS.primary} 100%)`,
              boxShadow: `0 6px 20px ${alpha(TOKENS.primary, 0.4)}`,
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
          },
          outlinedPrimary: {
            borderColor: alpha(TOKENS.primary, 0.4),
            '&:hover': {
              background: alpha(TOKENS.primary, 0.06),
              borderColor: TOKENS.primary,
            },
          },
        },
      },

      // ── TextField ───────────────────────────────────────────
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: TOKENS.radius,
            transition: 'box-shadow 0.2s ease',
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(TOKENS.primary, 0.15)}`,
            },
            '& fieldset': {
              borderColor: dark ? 'rgba(99,140,255,0.2)' : 'rgba(59,91,219,0.2)',
              transition: 'border-color 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: dark ? 'rgba(99,140,255,0.4)' : alpha(TOKENS.primary, 0.5),
            },
          },
        },
      },

      // ── Chip ────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.01em',
          },
        },
      },

      // ── Table ───────────────────────────────────────────────
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              background: dark ? 'rgba(99,140,255,0.05)' : alpha(TOKENS.primary, 0.04),
              borderBottom: `2px solid ${dark ? 'rgba(99,140,255,0.1)' : alpha(TOKENS.primary, 0.1)}`,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background 0.15s ease',
            '&:hover': {
              background: dark ? 'rgba(99,140,255,0.04)' : alpha(TOKENS.primary, 0.03),
            },
          },
        },
      },

      // ── Tabs ────────────────────────────────────────────────
      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${dark ? 'rgba(99,140,255,0.1)' : 'rgba(59,91,219,0.1)'}`,
          },
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
            background: `linear-gradient(90deg, ${TOKENS.primary}, #5c7cfa)`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: "'Syne', sans-serif",
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '-0.01em',
            textTransform: 'none',
            minHeight: 48,
            transition: 'color 0.2s ease',
          },
        },
      },

      // ── Dialog ──────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: TOKENS.radius + 4,
            boxShadow: dark
              ? '0 24px 64px rgba(0,0,0,0.6)'
              : '0 24px 64px rgba(12,17,32,0.2)',
          },
        },
      },

      // ── Alert ───────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: TOKENS.radius,
            border: '1px solid currentColor',
            borderColor: 'transparent',
          },
          standardError:   { borderColor: alpha(TOKENS.danger, 0.2) },
          standardWarning: { borderColor: alpha(TOKENS.warning, 0.2) },
          standardSuccess: { borderColor: alpha(TOKENS.success, 0.2) },
          standardInfo:    { borderColor: alpha(TOKENS.info, 0.2) },
        },
      },

      // ── Tooltip ─────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: '0.8rem',
            fontWeight: 500,
            padding: '6px 12px',
            background: dark ? '#1a2540' : '#0c1120',
          },
          arrow: {
            color: dark ? '#1a2540' : '#0c1120',
          },
        },
      },

      // ── Menu ────────────────────────────────────────────────
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: TOKENS.radius + 4,
            border: `1px solid ${dark ? 'rgba(99,140,255,0.1)' : 'rgba(59,91,219,0.08)'}`,
            boxShadow: dark
              ? '0 16px 48px rgba(0,0,0,0.5)'
              : '0 16px 48px rgba(12,17,32,0.15)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 6px',
            padding: '8px 12px',
            transition: 'background 0.15s ease',
          },
        },
      },

      // ── LinearProgress ──────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 99, height: 6 },
          bar: { borderRadius: 99 },
        },
      },

      // ── CircularProgress ────────────────────────────────────
      MuiCircularProgress: {
        defaultProps: { thickness: 3.5 },
      },
    },
  });
}

// ─── Provider ────────────────────────────────────────────────
export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);
  const theme = buildTheme(darkMode);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
